import { AddButton } from "../../components/AddButton";
import { ChannelRecord } from "../channels/types";
import {
	Datagrid,
	EditButton,
	Pagination,
	ReferenceField,
	ReferenceManyField,
	Show,
	SimpleShowLayout,
	Tab,
	TabbedShowLayout,
	TextField,
	TopToolbar,
	useRecordContext,
} from "react-admin";
import { OrganEvents } from "./OrganEvents";
import { OrganRecord } from "./types";
import { RemoveWithConfirmButton } from "../../components/RemoveWithConfirmButton";
import { useGlobalPermission, useOrganPermission } from "../../permissions";

const OrganShowActions = () => {
	const organEditPermission = useGlobalPermission("organs.update");

	return <TopToolbar>{organEditPermission && <EditButton />}</TopToolbar>;
};

const RemoveChannelFromOrganButton = () => {
	const channel = useRecordContext<ChannelRecord>();
	const { hasPermissionInOrgan } = useOrganPermission("channels.delete");

	return hasPermissionInOrgan(channel.organ_id) ? (
		<RemoveWithConfirmButton
			confirmContent="Are you sure you want to remove the channel?"
			notifyMessage="Channel removed"
		/>
	) : null;
};

const RemoveTeamFromOrganButton = () => {
	const teamOrganDeletePermission = useGlobalPermission("organ-teams.delete");

	return teamOrganDeletePermission ? (
		<RemoveWithConfirmButton
			confirmContent="Are you sure you want to disassociate the team?"
			notifyMessage="Team disassociated"
		/>
	) : null;
};

const AddChannelToOrganButton = () => {
	const organ = useRecordContext<OrganRecord>();
	const { hasPermissionInOrgan } = useOrganPermission("channels.create");

	return hasPermissionInOrgan(organ.id) ? (
		<AddButton
			resource="channels"
			foreignKey="organ_id"
			label="Add new channel"
		/>
	) : null;
};

const AddTeamToOrganButton = () => {
	return useGlobalPermission("organ-teams.create") ? (
		<AddButton
			resource="organ-teams"
			foreignKey="organ_id"
			label="Add new team"
		/>
	) : null;
};

export const OrganShow = () => {
	return (
		<Show title="Organ view" actions={<OrganShowActions />}>
			<SimpleShowLayout>
				<TextField source="name" />
				<TextField source="abbreviation" />
			</SimpleShowLayout>
			<TabbedShowLayout>
				<Tab label="Teams">
					<ReferenceManyField
						label={false}
						reference="organ-teams"
						target="organ_id"
						pagination={<Pagination />}
					>
						<Datagrid
							bulkActionButtons={false}
							rowClick={(id, resource, record) =>
								`/teams/${record.team_id}/show`
							}
						>
							<ReferenceField
								source="team_id"
								reference="teams"
								link="show"
								label="Team Name"
							>
								<TextField source="name" />
							</ReferenceField>
							<RemoveTeamFromOrganButton />
						</Datagrid>
					</ReferenceManyField>
					<AddTeamToOrganButton />
				</Tab>
				<Tab label="Channels" path="channels">
					<ReferenceManyField
						label={false}
						reference="channels"
						target="organ_id"
					>
						<Datagrid bulkActionButtons={false} rowClick="show">
							<TextField label="Channel" source="name" />
							<RemoveChannelFromOrganButton />
						</Datagrid>
					</ReferenceManyField>
					<AddChannelToOrganButton />
				</Tab>
				<Tab label="Events" path="events">
					<OrganEvents />
				</Tab>
			</TabbedShowLayout>
		</Show>
	);
};
